import {
  ProgressStateAdm,
  ProgressStateDtg,
  ProgressStateGbd,
  ProgressStateVda,
} from "@root/misc/constants";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AdmStateInfo } from "./AdmStateInfo";
import { DtgStateInfo } from "./DtgStateInfo";
import { GbdStateInfo } from "./GbdStateInfo";
import style from "./style.module.css";

type Variant = "home" | "on-location";
type Lang = "nl" | "en";

/** Create a link to a step within a game.
 *
 * @param basePath URL path of a step in a game
 * @param states possible states of the game as defined in `constants.ts`
 * @param token token to include in the link
 * @returns UI fragment that points to said step
 */
const renderSteps = (
  basePath: string,
  states: Record<string, string>,
  token: string,
): JSX.Element[] =>
  Object.values(states).map((state) => (
    <li key={`${basePath}-${state}`}>
      <a href={`${basePath}?token=${token}&step=${state}`}>{state}</a>
    </li>
  ));

/** A handy menu that sets up a quick access panel to jump to a game and a step within a game. */
export function DevMenu(): JSX.Element | null {
  const location = useLocation();
  const navigate = useNavigate();
  const [variant, setVariant] = React.useState<Variant>(
    location.pathname.includes("home") ? "home" : "on-location",
  );

  const handleVariantChange = (next: Variant): void => {
    setVariant(next);
    const segments = location.pathname.split("/");
    if (segments[2] === "home" || segments[2] === "on-location") {
      segments[2] = next;
      navigate(`${segments.join("/")}${location.search}`);
    }
  };

  const params = new URLSearchParams(location.search);
  const lang = (params.get("lang") as Lang | null) ?? "nl";
  const token = params.get("token") ?? "";

  const handleLangChange = (next: Lang): void => {
    const nextParams = new URLSearchParams(location.search);
    nextParams.set("lang", next);
    window.location.assign(`${location.pathname}?${nextParams.toString()}`);
  };

  // Show in dev mode and in the explicit "test" production bundle. Hide in real production.
  const isProdBuild = process.env.NODE_ENV === "production";
  const isTestEnv = process.env.REACT_APP_ENV === "test";
  if (isProdBuild && !isTestEnv) {
    return null;
  }

  return (
    <div className={style.debug}>
      <nav>
        {location.pathname.startsWith("/dtg") && <DtgStateInfo />}
        {location.pathname.startsWith("/adm") && <AdmStateInfo />}
        {location.pathname.startsWith("/gbd") && <GbdStateInfo />}
        <ul className={style.screenList}>
          <li>
            <a href={`/introduction?token=${token}`}>Introductie</a>
          </li>
          <li>
            <details>
              <summary>Gbd</summary>
              <ul className={style.stepList}>
                <li>
                  <a href={`/gbd/${variant}?token=${token}`}>Gbd</a>
                </li>
                {renderSteps(`/gbd/${variant}`, ProgressStateGbd, token)}
                <li>
                  <a href={`/gbd/${variant}/4?token=${token}`}>Gbd - 4 pagina's</a>
                </li>
              </ul>
            </details>
          </li>
          <li>
            <details>
              <summary>Adm</summary>
              <ul className={style.stepList}>
                <li>
                  <a href={`/adm/${variant}/30?token=${token}`}>Adm</a>
                </li>
                {renderSteps(`/adm/${variant}/30`, ProgressStateAdm, token)}
              </ul>
            </details>
          </li>
          <li>
            <details>
              <summary>Dtg</summary>
              <ul className={style.stepList}>
                <li>
                  <a href={`/dtg/${variant}?token=${token}`}>Dtg</a>
                </li>
                {renderSteps(`/dtg/${variant}`, ProgressStateDtg, token)}
                <li>
                  <a href={`/dtg/${variant}/10;10;10/1100;1000;800/20?token=${token}`}>
                    Dtg (custom invoer)
                  </a>
                </li>
              </ul>
            </details>
          </li>
          <li>
            <details>
              <summary>Vda</summary>
              <ul className={style.stepList}>
                <li>
                  <a href={`/vda/${variant}?token=${token}`}>Vda</a>
                </li>
                {renderSteps(`/vda/${variant}`, ProgressStateVda, token)}
              </ul>
            </details>
          </li>
        </ul>
      </nav>
      <hr />
      <label className={style["variant-picker"]}>
        Variant:{" "}
        <select value={variant} onChange={(e) => handleVariantChange(e.target.value as Variant)}>
          <option value="home">home</option>
          <option value="on-location">on-location</option>
        </select>
      </label>
      <hr />
      <label className={style["variant-picker"]}>
        Language:{" "}
        <select value={lang} onChange={(e) => handleLangChange(e.target.value as Lang)}>
          <option value="nl">nl</option>
          <option value="en">en</option>
        </select>
      </label>
    </div>
  );
}
